'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { QueriesKeys } from '@/queries/queriesKeys';
import { formatHoursMinutes, getNextChicagoMidnightIso } from '../utils/dailyBonusRewards.utils';

const getFormattedTimeRemaining = (targetDate: string): string => {
  const diffInMs = Math.max(new Date(targetDate).getTime() - Date.now(), 0);
  const totalSeconds = Math.floor(diffInMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  return formatHoursMinutes({ hours, minutes }) ?? '00:00';
};

const DailyBonusTimer = () => {
  const queryClient = useQueryClient();
  const timerRef = useRef<HTMLSpanElement>(null);
  const initialTargetDate = useMemo(() => getNextChicagoMidnightIso(), []);

  useEffect(() => {
    let targetDate = initialTargetDate;
    let expiredTargetDate: string | null = null;

    const updateTimer = () => {
      if (!timerRef.current) {
        return;
      }

      if (new Date(targetDate).getTime() <= Date.now()) {
        if (expiredTargetDate !== targetDate) {
          expiredTargetDate = targetDate;
          queryClient.invalidateQueries({ queryKey: [QueriesKeys.rewardsGrouped] });
        }

        targetDate = getNextChicagoMidnightIso();
      }

      timerRef.current.textContent = getFormattedTimeRemaining(targetDate);
    };

    updateTimer();
    const intervalId = setInterval(updateTimer, 1000);

    return () => clearInterval(intervalId);
  }, [initialTargetDate, queryClient]);

  return (
    <span ref={timerRef} suppressHydrationWarning className="text-[0.625rem] leading-4 font-medium text-white">
      --:--
    </span>
  );
};

export default DailyBonusTimer;
